import { useRef } from 'react';
import Popover from '@ui/popover/popover';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';

const DURATION_OPTIONS = [
  { label: 'Permanent', value: 'permanent' },
  { label: 'Pulse', value: 'pulse' },
];

interface WatermarkDurationSelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}

const WatermarkDurationSelector = ({
  value,
  onChange,
  label = 'Duration',
  disabled,
}: WatermarkDurationSelectorProps) => {
  const portalRef = useRef<HTMLDivElement | null>(null);
  const selectedLabel = DURATION_OPTIONS.find((o) => o.value === value)?.label ?? value;

  return (
    <Container className="flex-column gap-2">
      <Text className="L2 padding-l-16" design="GlobalIcoText3">
        {label}
      </Text>
      <Popover
        closeOnClick
        width="100%"
        popoverWidth="100%"
        portalEl={portalRef}
        disabled={disabled}
        renderTriggerButton={({ open, closing }) => (
          <Button
            border="1px solid var(--global-ico-text-3)"
            hoverBorder="1px solid var(--global-ico-text-4)"
            height="36px"
            width="100%"
            clear
            className="flex ai-center"
            borderRadius="100px"
            disabled={disabled}
            disabledOpacity={0.6}
          >
            <div className="flex flex-1 ai-center jc-space-between gap-6 padding-h-16">
              <Text className="P2" design="GlobalIcoText1">
                {selectedLabel}
              </Text>
              <RotatableIcon
                name={ICONS.KEYBOARD_ARROW_DOWN}
                size={12}
                color="var(--global-ico-text-1)"
                rotate={open && !closing ? '180deg' : '0'}
              />
            </div>
          </Button>
        )}
      >
        {({ triggerBounds }) => (
          <Container
            background="var(--gray-800)"
            borderRadius="12px"
            className="position-fixed flex-column overflow-hidden"
            style={{
              top: (triggerBounds?.bottom ?? 0) + 4,
              left: triggerBounds?.left ?? 0,
              width: triggerBounds?.width ?? 160,
              zIndex: 100000,
            }}
          >
            {DURATION_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                width="100%"
                className="radius-0 text-left flex ai-center"
                backgroundColor={opt.value === value ? 'var(--gray-700)' : 'transparent'}
                hoverBackgroundColor="var(--gray-700)"
                onClick={() => onChange(opt.value)}
              >
                <Text design="GlobalIcoText1" className="S2 flex-1">
                  {opt.label}
                </Text>
                {opt.value === value && <Icon className="position-relative left-2" name={ICONS.CHECK} size={12} />}
              </Button>
            ))}
          </Container>
        )}
      </Popover>
    </Container>
  );
};

export default WatermarkDurationSelector;
